import React from "react";
import type { HTMLAttributes, ReactNode } from "react";

import styles from "./StatusBadge.module.css";

export type StatusBadgeTone =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "neutral";

export type StatusBadgeVariant = "solid" | "soft" | "outline";
export type StatusBadgeSize = "sm" | "md";

const toneClassMap: Record<StatusBadgeTone, string> = {
  success: styles.success,
  danger: styles.danger,
  warning: styles.warning,
  info: styles.info,
  neutral: styles.neutral,
};

const variantClassMap: Record<StatusBadgeVariant, string> = {
  solid: styles.solid,
  soft: styles.soft,
  outline: styles.outline,
};

const sizeClassMap: Record<StatusBadgeSize, string> = {
  sm: styles.sm,
  md: styles.md,
};

export interface StatusBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "color"> {
  children: ReactNode;
  /** Semantic tone — preferred path. Wins over `color` if both are passed. */
  tone?: StatusBadgeTone;
  /** Escape hatch: hex, CSS var, or antd preset name (gold/blue/purple/etc.). */
  color?: string;
  variant?: StatusBadgeVariant;
  size?: StatusBadgeSize;
}

/** antd Tag preset color names → hex values. Lets us migrate `<Tag color="gold">` etc. */
const ANTD_PRESETS: Record<string, string> = {
  gold: "#faad14",
  blue: "#1677ff",
  green: "#52c41a",
  purple: "#722ed1",
  red: "#ff4d4f",
  orange: "#fa8c16",
  cyan: "#13c2c2",
  magenta: "#eb2f96",
  yellow: "#fadb14",
  lime: "#a0d911",
  geekblue: "#2f54eb",
  volcano: "#fa541c",
};

const resolveColor = (color: string): string => ANTD_PRESETS[color] ?? color;

function StatusBadge({
  children,
  tone,
  color,
  variant = "soft",
  size = "md",
  className = "",
  style,
  ...rest
}: StatusBadgeProps) {
  const useTone = tone !== undefined;

  const inlineStyle: React.CSSProperties = useTone
    ? { ...style }
    : color
      ? { ...style, ["--badge-color" as string]: resolveColor(color) }
      : { ...style };

  const toneClass = useTone ? toneClassMap[tone] : styles.custom;

  const finalClassName = [
    styles.badge,
    variantClassMap[variant],
    sizeClassMap[size],
    toneClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={finalClassName} style={inlineStyle} {...rest}>
      {children}
    </span>
  );
}

export default StatusBadge;
